/**
 * Theme Utilities
 * Helper functions for generating styles from theme
 */

import React from 'react';

import { EmbedTheme } from './types';

// ============================================================================
// Style Generator Types
// ============================================================================

export type StyleGenerator<P = object> = (theme: EmbedTheme, props?: P) => React.CSSProperties;

// ============================================================================
// Common Style Generators
// ============================================================================

/**
 * Generates base page container styles
 */
export const pageContainerStyles: StyleGenerator = (theme) => ({
  width: '100%',
  height: '100%',
  backgroundColor: theme.colors.background.page,
  color: theme.colors.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.fontSize.base,
  lineHeight: theme.typography.lineHeight.normal,
});

/**
 * Generates card/surface styles
 */
export const cardStyles: StyleGenerator<{ elevated?: boolean }> = (theme, props) => ({
  backgroundColor: props?.elevated ? theme.colors.background.elevated : theme.colors.background.surface,
  borderRadius: theme.components.card.borderRadius,
  border: `${theme.borderWidth.base} solid ${theme.colors.border.default}`,
  boxShadow: props?.elevated ? theme.shadow.md : theme.shadow.base,
  padding: theme.components.card.padding,
});

/**
 * Generates primary button styles
 */
export const primaryButtonStyles: StyleGenerator<{
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  fullWidth?: boolean;
}> = (theme, props) => {
  const size = props?.size || 'md';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['2'],
    height: theme.components.button.height[size],
    padding: theme.components.button.padding[size],
    backgroundColor: props?.disabled ? theme.colors.text.tertiary : theme.colors.palette.primary,
    color: theme.colors.text.inverse,
    border: 'none',
    borderRadius: theme.components.button.borderRadius,
    fontSize: size === 'sm' ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    cursor: props?.disabled ? 'not-allowed' : 'pointer',
    transition: `all ${theme.animation.duration.fast} ${theme.animation.easing.default}`,
    width: props?.fullWidth ? '100%' : 'auto',
    opacity: props?.disabled ? 0.6 : 1,
  };
};

/**
 * Generates secondary/outline button styles
 */
export const secondaryButtonStyles: StyleGenerator<{
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}> = (theme, props) => {
  const size = props?.size || 'md';
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['2'],
    height: theme.components.button.height[size],
    padding: theme.components.button.padding[size],
    backgroundColor: 'transparent',
    color: theme.colors.text.primary,
    border: `${theme.borderWidth.base} solid ${theme.colors.border.default}`,
    borderRadius: theme.components.button.borderRadius,
    fontSize: size === 'sm' ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    cursor: props?.disabled ? 'not-allowed' : 'pointer',
    transition: `all ${theme.animation.duration.fast} ${theme.animation.easing.default}`,
    opacity: props?.disabled ? 0.6 : 1,
  };
};

/**
 * Generates text input styles
 */
export const inputStyles: StyleGenerator<{ hasError?: boolean }> = (theme, props) => ({
  width: '100%',
  height: theme.components.input.height,
  padding: theme.components.input.padding,
  backgroundColor: theme.colors.background.input,
  color: theme.colors.text.primary,
  border: `${theme.borderWidth.base} solid ${
    props?.hasError ? theme.colors.border.error : theme.colors.border.default
  }`,
  borderRadius: theme.components.input.borderRadius,
  fontSize: theme.typography.fontSize.base,
  fontFamily: theme.typography.fontFamily,
  outline: 'none',
  transition: `border-color ${theme.animation.duration.fast} ${theme.animation.easing.default}`,
});

/**
 * Generates avatar styles
 */
export const avatarStyles: StyleGenerator<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'rounded-sm';
}> = (theme, props) => {
  const size = props?.size || 'md';
  const dimension = theme.components.avatar[size];
  return {
    width: dimension,
    height: dimension,
    borderRadius: props?.shape === 'rounded-sm' ? theme.borderRadius.lg : theme.borderRadius.full,
    overflow: 'hidden',
    backgroundColor: theme.colors.background.subtle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
};

/**
 * Generates heading text styles
 */
export const headingStyles: StyleGenerator<{
  level?: 1 | 2 | 3 | 4;
}> = (theme, props) => {
  const sizeMap = {
    1: theme.typography.fontSize['3xl'],
    2: theme.typography.fontSize['2xl'],
    3: theme.typography.fontSize.xl,
    4: theme.typography.fontSize.lg,
  };
  return {
    fontSize: sizeMap[props?.level || 1],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.text.primary,
    margin: 0,
    lineHeight: theme.typography.lineHeight.tight,
  };
};

/**
 * Generates body text styles
 */
export const textStyles: StyleGenerator<{
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'xs' | 'sm' | 'base' | 'lg';
}> = (theme, props) => {
  const colorMap = {
    primary: theme.colors.text.primary,
    secondary: theme.colors.text.secondary,
    tertiary: theme.colors.text.tertiary,
  };
  return {
    fontSize: theme.typography.fontSize[props?.size || 'base'],
    color: colorMap[props?.variant || 'primary'],
    margin: 0,
    lineHeight: theme.typography.lineHeight.normal,
  };
};

/**
 * Generates flex container styles
 */
export const flexStyles: StyleGenerator<{
  direction?: 'row' | 'column';
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  gap?: keyof typeof import('./defaultTheme').SPACING;
  wrap?: boolean;
}> = (theme, props) => {
  const alignMap = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
  };
  const justifyMap = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
    around: 'space-around',
  };
  return {
    display: 'flex',
    flexDirection: props?.direction || 'row',
    alignItems: alignMap[props?.align || 'stretch'],
    justifyContent: justifyMap[props?.justify || 'start'],
    gap: props?.gap ? theme.spacing[props.gap] : undefined,
    flexWrap: props?.wrap ? 'wrap' : 'nowrap',
  };
};

/**
 * Generates loading spinner styles
 */
export const spinnerStyles: StyleGenerator<{ size?: number }> = (theme, props) => ({
  width: `${props?.size || 32}px`,
  height: `${props?.size || 32}px`,
  border: `3px solid ${theme.colors.border.default}`,
  borderTopColor: theme.colors.palette.primary,
  borderRadius: theme.borderRadius.full,
  animation: 'embed-spin 1s linear infinite',
});

/**
 * Generates divider/separator styles
 */
export const dividerStyles: StyleGenerator<{ vertical?: boolean }> = (theme, props) => ({
  backgroundColor: theme.colors.border.default,
  ...(props?.vertical ? { width: '1px', height: '100%' } : { height: '1px', width: '100%' }),
});

/**
 * Generates error message styles
 */
export const errorStyles: StyleGenerator = (theme) => ({
  padding: theme.spacing['3'],
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  border: `${theme.borderWidth.base} solid ${theme.colors.border.error}`,
  borderRadius: theme.borderRadius.md,
  color: theme.colors.text.error,
  fontSize: theme.typography.fontSize.sm,
});

/**
 * Generates success message styles
 */
export const successStyles: StyleGenerator = (theme) => ({
  padding: theme.spacing['3'],
  backgroundColor: 'rgba(34, 197, 94, 0.1)',
  border: `${theme.borderWidth.base} solid rgba(34, 197, 94, 0.3)`,
  borderRadius: theme.borderRadius.md,
  color: theme.colors.text.success,
  fontSize: theme.typography.fontSize.sm,
});

// ============================================================================
// Component-Specific Style Generators
// ============================================================================

/**
 * Generates header bar styles
 */
export const headerStyles: StyleGenerator = (theme) => ({
  padding: theme.spacing['4'],
  borderBottom: `${theme.borderWidth.base} solid ${theme.colors.border.default}`,
  backgroundColor: theme.colors.background.surface,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

/**
 * Generates message bubble styles
 * Note: maxWidth is handled by the parent container, not the bubble itself
 */
export const messageBubbleStyles: StyleGenerator<{ isUser?: boolean }> = (theme, props) => ({
  padding: `${theme.spacing['2']} ${theme.spacing['3']}`,
  borderRadius: props?.isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
  backgroundColor: props?.isUser ? theme.colors.palette.primary : theme.colors.background.subtle,
  color: props?.isUser ? theme.colors.text.inverse : theme.colors.text.primary,
  fontSize: theme.typography.fontSize.base,
  lineHeight: theme.typography.lineHeight.normal,
  wordBreak: 'break-word',
});

/**
 * Generates modal overlay styles
 */
export const modalOverlayStyles: StyleGenerator = (theme) => ({
  position: 'absolute' as const,
  inset: 0,
  backgroundColor: theme.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(249, 250, 251, 0.9)',
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Generates modal container styles
 */
export const modalContainerStyles: StyleGenerator = (theme) => ({
  width: '100%',
  height: '100%',
  maxWidth: '400px',
  maxHeight: '600px',
  backgroundColor: theme.colors.background.surface,
  borderRadius: theme.borderRadius.xl,
  overflow: 'hidden',
  position: 'relative' as const,
  boxShadow: theme.shadow.xl,
});

// ============================================================================
// Global Styles Generator
// ============================================================================

/**
 * Generates global CSS keyframes and styles as a string
 */
export function generateGlobalStyles(theme: EmbedTheme): string {
  return `
    @keyframes embed-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes embed-slide-down {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
    @keyframes embed-bounce {
      0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.4;
      }
      40% {
        transform: scale(1);
        opacity: 1;
      }
    }
    @keyframes embed-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes embed-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .bolder-embed-app * {
      box-sizing: border-box;
    }
    .bolder-embed-app ::-webkit-scrollbar {
      width: 4px;
    }
    .bolder-embed-app ::-webkit-scrollbar-track {
      background: transparent;
    }
    .bolder-embed-app ::-webkit-scrollbar-thumb {
      background: ${theme.mode === 'dark' ? '#4b5563' : '#d1d5db'};
      border-radius: 2px;
    }
  `;
}

// ============================================================================
// Merge Styles Utility
// ============================================================================

/**
 * Merges multiple style objects, with later objects taking precedence
 */
export function mergeStyles(...styles: (React.CSSProperties | undefined)[]): React.CSSProperties {
  return Object.assign({}, ...styles.filter(Boolean));
}

/**
 * Creates a style with hover state support via data attributes
 */
export function withHoverStyles(
  baseStyles: React.CSSProperties,
  hoverStyles: React.CSSProperties,
): {
  style: React.CSSProperties;
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: (e: React.MouseEvent<HTMLElement>) => void;
} {
  return {
    style: baseStyles,
    onMouseEnter: (e) => {
      Object.assign(e.currentTarget.style, hoverStyles);
    },
    onMouseLeave: (e) => {
      Object.assign(e.currentTarget.style, baseStyles);
    },
  };
}
